import React, { useState } from 'react';
import { Package, Truck, CheckCircle2, Search, ExternalLink } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useLocale } from '../context/LocaleContext';
import api from '../api/client';

const STAGES = ['placed', 'confirmed', 'shipped', 'delivered'];
const STAGE_ICONS = { placed: Package, confirmed: CheckCircle2, shipped: Truck, delivered: CheckCircle2 };

const TrackOrder = () => {
  const { t } = useLocale();
  const [orderId, setOrderId] = useState('');
  const [email, setEmail] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const lookup = async (e) => {
    e.preventDefault();
    setError('');
    if (!orderId.trim() || !email.trim()) {
      setError(t('trackFillBoth'));
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get(`/orders/track/${encodeURIComponent(orderId.trim())}`, { params: { email: email.trim() } });
      setResult(data);
    } catch (err) {
      setError(err.response?.status === 404 ? t('trackNotFound') : (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const currentStageIdx = result ? Math.max(0, STAGES.indexOf(result.status)) : -1;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-3xl mx-auto px-6 py-14" data-testid="track-order-page">
      <section className="text-center mb-10">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight">{t('trackTitle')}</h1>
        <div className="h-1 w-16 bg-[#22c55e] mx-auto mt-3" />
        <p className="text-gray-600 mt-4">{t('trackSubtitle')}</p>
      </section>

      <form onSubmit={lookup} className="bg-white border rounded-2xl p-6 md:p-8 space-y-4 mb-10">
        <div>
          <label className="text-xs font-bold text-gray-700">{t('trackOrderId')} *</label>
          <Input placeholder="e.g. a1b2c3d4-..." value={orderId} onChange={(e) => setOrderId(e.target.value)} data-testid="track-order-id" />
        </div>
        <div>
          <label className="text-xs font-bold text-gray-700">{t('trackBuyerEmail')} *</label>
          <Input type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="track-email" />
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-[#22c55e] hover:bg-[#16a34a] text-black font-black py-6 tracking-wider" data-testid="track-submit">
          <Search size={16} className="mr-2" />
          {loading ? '...' : t('trackSubmit')}
        </Button>
        {error && <p className="text-sm text-red-600 font-medium" data-testid="track-error">{error}</p>}
      </form>

      {result && (
        <section className="bg-white border rounded-2xl p-6 md:p-8" data-testid="track-result">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-6">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">Order</div>
              <div className="font-black text-lg">#{result.order_id.slice(0, 8).toUpperCase()}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500 uppercase tracking-wider">Placed</div>
              <div className="font-semibold text-sm">{new Date(result.created_at).toLocaleDateString()}</div>
            </div>
          </div>

          {/* Status timeline */}
          <div className="grid grid-cols-4 gap-2 mb-8">
            {STAGES.map((s, i) => {
              const Icon = STAGE_ICONS[s] || Package;
              const done = i <= currentStageIdx;
              return (
                <div key={s} className="text-center" data-testid={`track-stage-${s}`}>
                  <div className={`w-11 h-11 mx-auto rounded-full border-2 flex items-center justify-center transition ${done ? 'bg-[#22c55e] border-[#22c55e] text-black' : 'border-gray-300 text-gray-400'}`}>
                    <Icon size={18} />
                  </div>
                  <div className={`text-[11px] font-bold mt-2 uppercase tracking-wider ${done ? 'text-black' : 'text-gray-400'}`}>{t(`trackStage_${s}`)}</div>
                </div>
              );
            })}
          </div>

          {/* Courier details */}
          {result.tracking && (result.tracking.courier || result.tracking.tracking_number) ? (
            <div className="bg-neutral-50 border rounded-xl p-5">
              <h3 className="font-black text-sm tracking-wider mb-3">{t('trackShipmentDetails')}</h3>
              {result.tracking.courier && (
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-sm text-gray-600">{t('trackCourier')}</span>
                  <span className="text-sm font-bold">{result.tracking.courier}</span>
                </div>
              )}
              {result.tracking.tracking_number && (
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-sm text-gray-600">{t('trackNumber')}</span>
                  <span className="text-sm font-bold font-mono">{result.tracking.tracking_number}</span>
                </div>
              )}
              {result.tracking.estimated_delivery && (
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-sm text-gray-600">{t('trackEta')}</span>
                  <span className="text-sm font-bold">{result.tracking.estimated_delivery}</span>
                </div>
              )}
              {result.tracking.notes && (
                <div className="py-2 border-b border-gray-200">
                  <span className="text-sm text-gray-600 block mb-1">{t('trackNotes')}</span>
                  <span className="text-sm">{result.tracking.notes}</span>
                </div>
              )}
              {result.tracking.tracking_url && (
                <a href={result.tracking.tracking_url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-2 mt-4 text-sm font-black text-[#22c55e] hover:underline" data-testid="track-external-link">
                  <ExternalLink size={14} />
                  {t('trackOpenCourier')}
                </a>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 text-sm border-2 border-dashed rounded-xl">
              {t('trackNoDetailsYet')}
            </div>
          )}
        </section>
      )}
      </main>
      <Footer />
    </div>
  );
};

export default TrackOrder;
